import React from 'react';
import {
  IoManOutline,
  IoWomanOutline,
  IoEllipseOutline,
  IoLeafOutline,
  IoFlashOutline,
  IoFlameOutline,
  IoRocketOutline,
  IoCalendarOutline,
  IoAlertCircleOutline,
  IoBanOutline,
  IoBusinessOutline,
  IoBarbellOutline,
  IoHomeOutline,
  IoWalkOutline,
  IoHourglassOutline,
  IoSparklesOutline,
  IoOptionsOutline,
  IoTrendingDownOutline,
  IoTrendingUpOutline,
  IoTrophyOutline,
  IoRefreshOutline,
  IoTimeOutline,
  IoArrowForwardOutline,
  IoPulseOutline,
  IoHelpCircleOutline,
  IoBodyOutline,
  IoAccessibilityOutline,
  IoShieldCheckmarkOutline,
  IoSunnyOutline,
  IoMoonOutline,
  IoBedOutline,
  IoSwapHorizontalOutline,
  IoBatteryDeadOutline,
  IoClipboardOutline,
  IoAlarmOutline,
  IoBandageOutline,
  IoChatbubbleEllipsesOutline,
  IoCheckmarkCircleOutline,
  IoCheckmark,
  IoStar,
  IoVideocamOutline,
  IoBarChartOutline,
  IoChevronDownOutline,
  IoRibbonOutline,
  IoLockClosedOutline,
  IoEllipsisVertical,
} from 'react-icons/io5';

// ============================================================================
// PEÇAS DE UI REUTILIZÁVEIS — estilo FitClutch (ciano sobre fundo escuro).
// Mantidas num arquivo só de propósito: são pequenas, usadas em toda tela do
// onboarding/paywall, e ficar trocando de arquivo pra achar cada uma custaria
// mais do que ajuda.
// ============================================================================

export function ImagePlaceholder({ src, alt = '', className = '', label = 'Cole o link da imagem aqui' }) {
  if (src) {
    return <img src={src} alt={alt} className={className} />;
  }
  return (
    <div className={`img-placeholder rounded-xl2 ${className}`}>
      <span>🖼 {label}</span>
    </div>
  );
}

// Barra de progresso ÚNICA e contínua — é a nossa identidade visual, não a
// do Befit (segmentada). Mantida assim de propósito (pedido da Gladis,
// 01/10/2026: "vamos deixar a nossa barra, ela é única e diferencia o
// nosso app").
export function ProgressBar({ step, totalSteps }) {
  if (!step || !totalSteps) return null;
  const pct = Math.round((step / totalSteps) * 100);
  return (
    <div className="w-full h-1.5 bg-surface-2 rounded-full overflow-hidden">
      <div
        className="h-full bg-cyan-gradient transition-all duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Voltar"
      className="w-9 h-9 rounded-full bg-surface-2/80 flex items-center justify-center text-ink"
    >
      ‹
    </button>
  );
}

export function PrimaryButton({ children, onClick, disabled, pulse = true, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full py-4 rounded-full font-display font-bold text-bg bg-cyan-gradient
        disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform
        shadow-glow ${!disabled && pulse ? 'fc-cta-pulse' : ''} ${className}`}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, onClick, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`w-full py-4 rounded-full font-display font-semibold text-ink bg-surface-2
        active:scale-[0.98] transition-transform ${className}`}
    >
      {children}
    </button>
  );
}

// Cabeçalho do quiz (voltar + logo centralizada + barra de progresso) —
// redesenhado em 01/10/2026 pra seguir a estrutura do Befit (ver
// pesquisa-concorrentes.md / print da tela "What is your gender"): logo
// sempre visível no topo, barra de progresso numa linha própria embaixo.
// Mantido dentro do ScreenShell pra TODAS as telas numeradas do quiz
// herdarem automaticamente, sem repetir código em cada etapa.
export function ScreenShell({ children, onBack, progress, noPadding = false }) {
  const showHeader = onBack || progress;
  return (
    <div className="phone-frame flex flex-col safe-top safe-bottom">
      {showHeader && (
        <div className="shrink-0">
          <div className="px-5 pt-4 pb-2 relative flex items-center justify-center min-h-[36px]">
            {onBack && (
              <div className="absolute left-5">
                <BackButton onClick={onBack} />
              </div>
            )}
            <div className="flex items-center gap-2">
              <img src="/logo-fc.png" alt="FitClutch" className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg" />
              <span className="font-display font-extrabold text-base sm:text-lg tracking-tight">FitClutch</span>
            </div>
          </div>
          {progress && (
            <div className="px-5 pb-3">
              <ProgressBar step={progress.step} totalSteps={progress.totalSteps} />
            </div>
          )}
        </div>
      )}
      <div className={`flex-1 flex flex-col min-h-0 ${noPadding ? '' : 'px-5'}`}>{children}</div>
    </div>
  );
}

export function OptionCard({ icon, label, subtitle, badge, recommended, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl2 p-4 flex items-center gap-4 border transition-colors
        ${selected ? 'border-cyan bg-surface-2' : 'border-transparent bg-surface'}`}
    >
      {icon && (
        <div className="w-11 h-11 shrink-0 rounded-full bg-surface-2 flex items-center justify-center text-cyan">
          <IconGlyph name={icon} size={20} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-display font-semibold text-ink">{label}</span>
          {recommended && (
            <span className="text-[10px] uppercase tracking-wide bg-cyan/20 text-cyan px-2 py-0.5 rounded-full">
              Recomendado
            </span>
          )}
          {badge && (
            <span className="text-[10px] uppercase tracking-wide bg-surface-2 text-ink-muted px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {subtitle && <p className="text-sm text-ink-muted mt-0.5">{subtitle}</p>}
      </div>
    </button>
  );
}

// Ícones de linha (Ionicons outline, via react-icons/io5) no lugar de
// emoji — pedido da Gladis (01/10/2026): "os emojis poluem, muito
// colorido, quero mais sofisticado". MESMO conjunto de ícones que o
// fitclutch-mobile JÁ usa pro quiz (ver
// fitclutch-mobile/src/config/quizIconMap.js, que traduz 'ti-*' pro
// equivalente Ionicons/@expo/vector-icons) — reaproveitado aqui de
// propósito pra manter os dois apps visualmente consistentes, em vez de
// inventar um conjunto de ícones novo. Ícones monocromáticos (herdam a cor
// do texto ao redor, aqui sempre ciano).
const ICON_COMPONENTS = {
  'ti-gender-male': IoManOutline,
  'ti-gender-female': IoWomanOutline,
  'ti-circle-dashed': IoEllipseOutline,
  'ti-leaf': IoLeafOutline,
  'ti-bolt': IoFlashOutline,
  'ti-flame': IoFlameOutline,
  'ti-rocket': IoRocketOutline,
  'ti-calendar-check': IoCalendarOutline,
  'ti-calendar-exclamation': IoAlertCircleOutline,
  'ti-ban': IoBanOutline,
  'ti-building-skyscraper': IoBusinessOutline,
  'ti-barbell': IoBarbellOutline,
  'ti-home': IoHomeOutline,
  'ti-walk': IoWalkOutline,
  'ti-calendar-2': IoCalendarOutline,
  'ti-calendar-3': IoCalendarOutline,
  'ti-calendar-4': IoCalendarOutline,
  'ti-hourglass': IoHourglassOutline,
  'ti-sparkles': IoSparklesOutline,
  'ti-adjustments': IoOptionsOutline,
  'ti-scale': IoTrendingDownOutline,
  'ti-trending-up': IoTrendingUpOutline,
  'ti-shoe': IoLeafOutline,
  'ti-trophy': IoTrophyOutline,
  'ti-refresh': IoRefreshOutline,
  'ti-calendar-time': IoTimeOutline,
  'ti-arrow-right': IoArrowForwardOutline,
  'ti-pulse': IoPulseOutline,
  'ti-trending-down': IoTrendingDownOutline,
  'ti-help': IoHelpCircleOutline,
  'ti-body': IoBodyOutline,
  'ti-accessibility': IoAccessibilityOutline,
  'ti-shield-check': IoShieldCheckmarkOutline,
  'ti-sunny': IoSunnyOutline,
  'ti-moon': IoMoonOutline,
  'ti-bed': IoBedOutline,
  'ti-swap': IoSwapHorizontalOutline,
  'ti-battery-low': IoBatteryDeadOutline,
  'ti-clipboard': IoClipboardOutline,
  'ti-alarm': IoAlarmOutline,
  'ti-bandage': IoBandageOutline,
  'ti-chat': IoChatbubbleEllipsesOutline,
  'ti-check-circle': IoCheckmarkCircleOutline,
  'ti-check': IoCheckmark,
  'ti-star': IoStar,
  'ti-video': IoVideocamOutline,
  'ti-chart': IoBarChartOutline,
  'ti-chevron-down': IoChevronDownOutline,
  'ti-ribbon': IoRibbonOutline,
  'ti-lock': IoLockClosedOutline,
  'ti-dots-vertical': IoEllipsisVertical,
};
export function IconGlyph({ name, size = 20, className = '' }) {
  const Icon = ICON_COMPONENTS[name] ?? IoEllipseOutline;
  return <Icon size={size} className={className} />;
}
